// Formats vidéo supersubvar disponibles pour les rédactions (générés via l'agent Mattermost @supersub).
// Ne lister que les formats live : ajouter une entrée quand un nouveau format passe en prod.
// Démos : `demoLangs` liste les langues dont la vidéo démo est disponible dans
// public/docs/footmercato/video_templates/<id>-<lang>.mp4 (+ image d'aperçu <id>-<lang>.jpg).
// Langue absente de `demoLangs` : la carte affiche un placeholder.
// Aide détaillée de chaque format : public/docs/footmercato/<lang>/video_templates/<id>.md

export type VideoTemplateLang = 'fr' | 'es' | 'de' | 'en';

export const VIDEO_TEMPLATE_LANGS: VideoTemplateLang[] = ['fr', 'es', 'de', 'en'];

interface LocalizedText {
  fr: string;
  es: string;
  de: string;
  en: string;
}

export interface VideoTemplateFormat {
  id: string;
  label: LocalizedText;
  description: LocalizedText;
  ratio: string;
  duration: LocalizedText;
  demoLangs?: VideoTemplateLang[];
}

export const helpDocPath = (lang: VideoTemplateLang, id: string) => `docs/footmercato/${lang}/video_templates/${id}.md`;

export const demoVideoPath = (lang: VideoTemplateLang, id: string) => `docs/footmercato/video_templates/${id}-${lang}.mp4`;

export const demoPosterPath = (lang: VideoTemplateLang, id: string) => `docs/footmercato/video_templates/${id}-${lang}.jpg`;

export const UI_TEXTS: { [key in VideoTemplateLang]: { [key: string]: string } } = {
  fr: {
    title: 'Templates vidéo',
    intro:
      'Pour générer une vidéo, envoie un message privé à @supersub sur Mattermost en commençant par « Fais une vidéo… » (format, joueur, match…). La vidéo te revient en MP4 dans la conversation. Clique sur « En savoir plus » pour voir les infos à fournir et les cas particuliers de chaque format. Accès sur demande : si @supersub te répond que tu n\'es pas autorisé, demande à Romain de t\'ajouter.',
    learnMore: 'En savoir plus',
    demoSoon: 'Démo à venir',
    close: 'Fermer',
  },
  es: {
    title: 'Plantillas de vídeo',
    intro:
      'Para generar un vídeo, envía un mensaje privado a @supersub en Mattermost empezando por «Haz un vídeo… para Fichajes» (formato, jugador, partido…). El vídeo te llega en MP4 en la conversación. Haz clic en «Más información» para ver los datos necesarios y los casos particulares de cada formato. Acceso bajo petición: si @supersub te responde que no estás autorizado, pide a Romain que te añada.',
    learnMore: 'Más información',
    demoSoon: 'Demo próximamente',
    close: 'Cerrar',
  },
  de: {
    title: 'Video-Vorlagen',
    intro:
      'Um ein Video zu erstellen, schreibe @supersub auf Mattermost eine Direktnachricht, die mit „Mach ein Video … für Fussball Transfers“ beginnt (Format, Spieler, Spiel …). Das Video kommt als MP4 in den Chat zurück. Klicke auf „Mehr erfahren“, um die benötigten Angaben und Besonderheiten jedes Formats zu sehen. Zugang auf Anfrage: Antwortet @supersub, dass du nicht berechtigt bist, bitte Romain, dich hinzuzufügen.',
    learnMore: 'Mehr erfahren',
    demoSoon: 'Demo folgt',
    close: 'Schließen',
  },
  en: {
    title: 'Video templates',
    intro:
      'To generate a video, send a direct message to @supersub on Mattermost starting with "Make a video… in English" (format, player, match…). The video comes back as an MP4 in the conversation. Click "Learn more" to see what to provide and the special cases for each format. Access on request: if @supersub replies that you are not authorised, ask Romain to add you.',
    learnMore: 'Learn more',
    demoSoon: 'Demo coming soon',
    close: 'Close',
  },
};

export const VIDEO_TEMPLATE_FORMATS: VideoTemplateFormat[] = [
  {
    id: 'transfer-16-9',
    label: {
      fr: 'Annonce de transfert',
      es: 'Anuncio de fichaje',
      de: 'Transfer-Ankündigung',
      en: 'Transfer announcement',
    },
    description: {
      fr: 'Rumeur ou officiel : photo au nouveau maillot, montant, stats et arrivée dans le XI du nouveau club.',
      es: 'Rumor u oficial: foto con la nueva camiseta, cantidad, estadísticas y llegada al once del nuevo club.',
      de: 'Gerücht oder offiziell: Foto im neuen Trikot, Ablöse, Statistiken und Platz in der Elf des neuen Klubs.',
      en: 'Rumour or official: photo in the new shirt, fee, stats and place in the new club’s starting XI.',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '~32 s', es: '~32 s', de: '~32 s', en: '~32 s' },
  },
  {
    id: 'match-lineups',
    label: { fr: 'Compositions', es: 'Alineaciones', de: 'Aufstellungen', en: 'Lineups' },
    description: {
      fr: 'Compos officielles ou probables avant le coup d’envoi, avec un face-à-face entre deux joueurs clés.',
      es: 'Alineaciones oficiales o probables antes del partido, con un cara a cara entre dos jugadores clave.',
      de: 'Offizielle oder voraussichtliche Aufstellungen vor dem Anpfiff, mit einem Duell zweier Schlüsselspieler.',
      en: 'Official or probable lineups before kick-off, with a head-to-head between two key players.',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '~37 s', es: '~37 s', de: '~37 s', en: '~37 s' },
  },
  {
    id: 'match-ratings',
    label: { fr: 'Notes du match', es: 'Notas del partido', de: 'Bewertungen zum Spiel', en: 'Player ratings' },
    description: {
      fr: 'Après le match : le score, les notes des 22 titulaires et l’homme du match.',
      es: 'Después del partido: el resultado, las notas de los 22 titulares y el jugador del partido.',
      de: 'Nach dem Spiel: das Ergebnis, die Noten der 22 Startspieler und der Spieler des Spiels.',
      en: 'After the match: the score, ratings for the 22 starters and the man of the match.',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '~31 s', es: '~31 s', de: '~31 s', en: '~31 s' },
  },
  {
    id: 'top-tweets',
    label: { fr: 'Top Tweets', es: 'Top Tweets', de: 'Top Tweets', en: 'Top Tweets' },
    description: {
      fr: 'Une sélection de 2 à 10 tweets qui défilent avec auteur, texte, image ou vidéo et engagement.',
      es: 'Una selección de 2 a 10 tuits que se suceden con autor, texto, imagen o vídeo e interacciones.',
      de: 'Eine Auswahl von 2 bis 10 Tweets nacheinander, mit Autor, Text, Bild oder Video und Interaktionen.',
      en: 'A selection of 2 to 10 tweets shown one after another, with author, text, image or video and engagement.',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '20-75 s', es: '20-75 s', de: '20-75 s', en: '20-75 s' },
  },
  {
    id: 'top-10-stats',
    label: { fr: 'Top 10 Stats', es: 'Top 10 Stats', de: 'Top 10 Stats', en: 'Top 10 Stats' },
    description: {
      fr: 'Compte à rebours des 10 meilleurs joueurs d’une compétition sur une stat (buts, passes décisives…).',
      es: 'Cuenta atrás de los 10 mejores jugadores de una competición en una estadística (goles, asistencias…).',
      de: 'Countdown der 10 besten Spieler eines Wettbewerbs in einer Statistik (Tore, Vorlagen …).',
      en: 'Countdown of the 10 best players in a competition on one stat (goals, assists…).',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '~50 s', es: '~50 s', de: '~50 s', en: '~50 s' },
  },
  {
    id: 'best-xi',
    label: { fr: 'Équipe type', es: 'Once ideal', de: 'Top-Elf', en: 'Best XI' },
    description: {
      fr: 'L’équipe type d’une compétition ou d’une phase : 11 joueurs sur le terrain, puis un focus sur chacun.',
      es: 'El once ideal de una competición o de una fase: 11 jugadores en el campo y un foco sobre cada uno.',
      de: 'Die beste Elf eines Wettbewerbs oder einer Runde: 11 Spieler auf dem Platz, dann ein Fokus auf jeden.',
      en: 'The best XI of a competition or a round: 11 players on the pitch, then a focus on each one.',
    },
    ratio: '16:9',
    demoLangs: ['fr', 'es', 'de', 'en'],
    duration: { fr: '~70 s', es: '~70 s', de: '~70 s', en: '~70 s' },
  },
  {
    id: 'player-focus',
    label: { fr: 'Focus joueur', es: 'Ficha de jugador', de: 'Spielerporträt', en: 'Player profile' },
    description: {
      fr: 'La fiche d’un joueur : identité, stats de la saison, profil de jeu, parcours en clubs et palmarès.',
      es: 'La ficha de un jugador: identidad, estadísticas de la temporada, perfil de juego, trayectoria y palmarés.',
      de: 'Das Porträt eines Spielers: Steckbrief, Saisonstatistiken, Spielprofil, Karrierestationen und Titel.',
      en: 'A player profile: identity, season stats, playing profile, club career and honours.',
    },
    ratio: '16:9',
    duration: { fr: '35-60 s', es: '35-60 s', de: '35-60 s', en: '35-60 s' },
  },
];
